import React, { PureComponent } from 'react';
import MapButton from './MapButton';
import { toLonLat } from 'ol/proj';
import { getCenter } from 'ol/extent';
import { calculateAcres, round, updateState } from '../../../utils';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Point from 'ol/geom/Point';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ContrastPaper from '../../utils/ContrastPaper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { flip, translate, inBounds } from '../helpers/gridHelper';
import { ZoneType } from '../../../db';
import Feature from 'ol/Feature';
import { LineString, Polygon } from 'ol/geom';
import { HiOutlineViewGridAdd } from 'react-icons/hi';
import { MISSION_EVENTS, dispatchMissionUpdated } from '../../../missionEvents';
import { dispatchCoresNeedUpdate } from '../../../mapEvents';
import EventBus from '../../../EventBus';
import './styles/gridbutton.css';
import { getCurrentMission } from '../../../dataModelHelpers';
import { alertWarnConfirm } from '../../../alertDispatcher';
import { GridLayerFeature } from '../layers/GridLayer';
import { LocalAtRecord } from '../../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { SQ_M_PER_ACRE } from '../../../constants';

interface GridGenerationButtonProps {
  updateGridLayerFeatures: (features: GridLayerFeature[]) => void;
  gridLayerFeatures: GridLayerFeature[];
}

const SELECTED_POLY_ID_ALL = '_ALL_POLYS_SELECTED_';

interface GridGenerationButtonState {
  gridContainerOpen: boolean;
  acres: number;
  actual: number;
  density: number; // acres/sample
  angle: number; // degrees
  boundaryDist: number; // feet
  startCorner: string; // NW, NE, SE, SW could be enum or new type
  pathDirection: string; // N/S, E/W could be enum or new type
  shiftNorth: number; // feet
  shiftEast: number; // feet
  densityIncrement: number; // acres/sample
  angleIncrement: number; // degrees
  boundaryIncrement: number; // feet
  shiftIncrement: number; // feet
  upToDate: boolean;
  fields: Feature<Polygon>[];
  selectedPolyId: string;
  polygonShifts: Map<string, { shiftNorth: number; shiftEast: number }>; // Store individual polygon shifts by poly_id
}

export default class GridGenerationButton extends PureComponent<GridGenerationButtonProps, GridGenerationButtonState> {
  SERIALIZABLE_STATE_VARIABLES = ['gridContainerOpen'];

  constructor(props: GridGenerationButtonProps) {
    super(props);

    this.state = {
      gridContainerOpen: false,
      acres: 0,
      actual: 0,
      density: 0, // acres/sample
      angle: 0, // degrees
      boundaryDist: 0, // feet
      startCorner: 'NW',
      pathDirection: 'N/S',
      shiftNorth: 0, // feet
      shiftEast: 0, // feet
      densityIncrement: 0.1, // acres/sample
      angleIncrement: 5, // degrees
      boundaryIncrement: 5, // feet
      shiftIncrement: 5, // feet
      upToDate: false,
      fields: [],
      selectedPolyId: SELECTED_POLY_ID_ALL,
      polygonShifts: new Map(), // Store individual polygon shifts
    };

    this.toggleGridContainer = this.toggleGridContainer.bind(this);
    this.initFields = this.initFields.bind(this);
    this.handleShiftChange = this.handleShiftChange.bind(this);
  }

  /**
   * Handle shift changes for either specific polygon or all polygons
   * @param shiftType - 'north' or 'east'
   * @param delta - amount to change (positive or negative)
   */
  handleShiftChange(shiftType: 'north' | 'east', delta: number) {
    const selectedPolyId = this.state.selectedPolyId;
    const isNorth = shiftType === 'north';
    const currentValue = isNorth ? this.state.shiftNorth : this.state.shiftEast;
    const newValue = currentValue + delta;

    const newPolygonShifts = new Map(this.state.polygonShifts);

    if (selectedPolyId !== SELECTED_POLY_ID_ALL) {
      // Update the specific polygon (can have positive or negative poly_id)
      // Get the existing shift for this polygon, or use default values
      const existingShift = newPolygonShifts.get(selectedPolyId) || { shiftNorth: 0, shiftEast: 0 };

      newPolygonShifts.set(selectedPolyId, {
        shiftNorth: isNorth ? newValue : existingShift.shiftNorth,
        shiftEast: isNorth ? existingShift.shiftEast : newValue,
      });
    } else {
      // Update all polygons
      this.state.fields.forEach((field) => {
        const poly_id = field.getProperties().poly_id;
        newPolygonShifts.set(poly_id, {
          shiftNorth: isNorth ? newValue : this.state.shiftNorth,
          shiftEast: isNorth ? this.state.shiftEast : newValue,
        });
      });
    }

    this.setState({
      [isNorth ? 'shiftNorth' : 'shiftEast']: newValue,
      polygonShifts: newPolygonShifts,
      upToDate: false,
    } as any);
  }

  async componentDidMount() {
    EventBus.on(MISSION_EVENTS.UPDATED, this.initFields);
    await this.initFields();

    this.deserializeState();
  }

  async componentDidUpdate(prevProps: GridGenerationButtonProps) {
    const acres = calculateAcres(this.state.fields);

    if (acres !== this.state.acres) {
      this.setState({ acres });
    }

    const mission = getCurrentMission();

    if (this.state.gridContainerOpen && !this.state.density && mission && mission.job_id) {
      await this.getDensity();
    }

    if (!this.state.upToDate && this.state.gridContainerOpen && this.state.fields.length > 0) {
      this.generateGrid();
    }
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.UPDATED, this.initFields);

    this.serializeState();
  }

  /**
   * serialize the state into session
   */
  serializeState() {
    const serializedState: { [key: string]: any } = {};

    for (const stateKey of this.SERIALIZABLE_STATE_VARIABLES) {
      // @ts-ignore
      serializedState[stateKey] = this.state[stateKey];
    }

    localStorage.setItem('GridGenerationButtonState', JSON.stringify(serializedState));
  }

  /**
   * deserialize the state
   */
  deserializeState() {
    const serializedState = JSON.parse(localStorage.getItem('GridGenerationButtonState') || '{}');

    if (serializedState) {
      for (const stateKey of Object.keys(serializedState)) {
        this.setState(updateState(stateKey as keyof GridGenerationButtonState, serializedState[stateKey]));
      }
    }
  }

  async initFields() {
    const mission = getCurrentMission();

    if (!mission) {
      this.close();

      return;
    }

    const fieldZone = mission.getZones().find((sel) => sel.zone_type === ZoneType.FIELD);

    if (!fieldZone) {
      this.close();

      return;
    }

    const features = await fieldZone.to_feature();
    const fields = features.map((feature) => {
      const coords = feature.geometry.coordinates.map((coords) =>
        coords.map((ring) => {
          return ring;
        }),
      );
      const geom = new Polygon(coords);
      const olFeature = new Feature(geom);
      // Store the poly_id in the feature properties so we can access it later
      olFeature.setProperties({ poly_id: feature.properties.poly_id });

      return olFeature;
    });
    this.setState({ fields });
  }

  async toggleGridContainer() {
    if (!this.state.gridContainerOpen) {
      const mission = getCurrentMission();
      const job = mission?.getJob();

      if (job && (job.points_submitted_from_customer || !job.allowed_to_create)) {
        const definitelyOpen = await alertWarnConfirm(
          'This job should include points already. Are you sure you want to generate points?',
        );

        if (!definitelyOpen) {
          return;
        }
      }

      this.setState({ gridContainerOpen: true });
    } else {
      this.close();
    }
  }

  validate() {
    const validationMap = {
      density: { error: false, text: '' },
      angle: { error: false, text: '' },
      boundaryDist: { error: false, text: '' },
    };

    if (undefined === this.state.density || isNaN(this.state.density) || this.state.density <= 0) {
      validationMap.density = { error: true, text: 'Must be a positive number' };
    }

    if (undefined === this.state.angle || isNaN(this.state.angle) || this.state.angle < 0 || this.state.angle >= 90) {
      validationMap.angle = { error: true, text: 'Must be between 0 and 90' };
    }

    if (undefined === this.state.boundaryDist || isNaN(this.state.boundaryDist) || this.state.boundaryDist < 0) {
      validationMap.boundaryDist = { error: true, text: 'Must be a positive number' };
    }

    return validationMap;
  }

  reset() {
    this.setState({
      density: 0,
      angle: 0,
      boundaryDist: 0,
      startCorner: 'NW',
      pathDirection: 'N/S',
      shiftNorth: 0,
      shiftEast: 0,
      upToDate: false,
      selectedPolyId: SELECTED_POLY_ID_ALL,
      polygonShifts: new Map(),
    });
  }

  async getDensity() {
    const currentMission = getCurrentMission();

    if (!currentMission?.job_id) {
      return;
    }

    try {
      const record = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', currentMission.job_id);

      if (record && record.getFirst('Deal Density')) {
        this.setState({ density: record.getFirst('Deal Density') || 0 });
      } else {
        console.warn('No Deal Density set for job');
      }
    } catch (err) {
      console.error('Could not pull airtable record for deal density', err);
    }
  }

  generateGrid() {
    //Parse inputs
    let density = this.state.density;
    let angle = this.state.angle;
    let boundaryDistance = this.state.boundaryDist;
    const startCorner = this.state.startCorner; // 'NW', 'NE', 'SW', 'SE'
    const pathDirection = this.state.pathDirection; // 'N/S', 'E/W'

    // Input Checking
    if (
      density === undefined ||
      isNaN(density) ||
      density <= 0 ||
      isNaN(angle) ||
      angle < 0 ||
      isNaN(boundaryDistance) ||
      boundaryDistance < 0
    ) {
      this.props.updateGridLayerFeatures([]);
      console.log('Invalid inputs to generation');
      console.log(density, angle, boundaryDistance);
      this.setState({ upToDate: true });

      return;
    }

    // ensure angle is in the range [0,90)
    if (angle >= 90 || angle < 0) {
      angle = (angle + 90) % 90;
    }

    // Convert to proper units
    density = density * SQ_M_PER_ACRE; // acres to meters^2
    boundaryDistance = boundaryDistance / 3.2808; // feet to meters
    angle = (angle * Math.PI) / 180 + ((Math.PI / 2) % (Math.PI / 2)); // degrees to radians in range [0, PI/2)

    // ALWAYS process all polygons - we just apply different shifts to each
    const polygonsToProcess = this.state.fields.map((f) => {
      const poly_id = f.getProperties().poly_id;

      return { field: f, polyId: poly_id };
    });

    // Generate grid for each polygon
    let allPoints: Point[] = [];
    const allPlots: Polygon[] = [];

    for (const { field, polyId } of polygonsToProcess) {
      // Get shift values for this specific polygon using its poly_id
      const polygonShift = this.state.polygonShifts.get(polyId);
      let shiftNorth = polygonShift?.shiftNorth || 0;
      let shiftEast = polygonShift?.shiftEast || 0;

      // Create a single polygon or multipolygon for this field
      const fieldGeom = field.getGeometry()!.clone();
      const fieldPoly = new MultiPolygon([fieldGeom.getCoordinates()]);

      // Distance between grid points
      const pointDistance = Math.sqrt(density); // meters^2 to meters
      shiftNorth = ((shiftNorth % pointDistance) + pointDistance) % pointDistance;
      shiftEast = ((shiftEast % pointDistance) + pointDistance) % pointDistance;

      // create initial transform point
      const shiftVector = new Point([shiftEast, shiftNorth]);

      // rotate whole polygon opposite of the desired rotation
      fieldPoly.rotate(-1 * angle, getCenter(fieldPoly.getExtent()));
      shiftVector.rotate(-1 * angle, [0, 0]);

      // flip to change the start corner
      if (startCorner === 'NW') {
        fieldPoly.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), true, false));
        shiftVector.applyTransform((c, o, d) => flip(c, o, d, [0, 0], true, false));
      } else if (startCorner === 'NE') {
        fieldPoly.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), true, true));
        shiftVector.applyTransform((c, o, d) => flip(c, o, d, [0, 0], true, true));
      } else if (startCorner === 'SW') {
        // no transform necessary
      } else if (startCorner === 'SE') {
        fieldPoly.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), false, true));
        shiftVector.applyTransform((c, o, d) => flip(c, o, d, [0, 0], false, true));
      }

      // rotate and flip to change sample direction
      if ('N/S' === pathDirection) {
        fieldPoly.rotate(Math.PI / 2, getCenter(fieldPoly.getExtent()));
        fieldPoly.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), false, true));
        shiftVector.rotate(Math.PI / 2, [0, 0]);
        shiftVector.applyTransform((c, o, d) => flip(c, o, d, [0, 0], false, true));
      }

      // Find boundary coordinates
      const extent = fieldPoly.getExtent();
      const minx = extent[0];
      const miny = extent[1];
      const maxx = extent[2];
      const maxy = extent[3];

      // Create grid of points
      const points: Point[] = [];
      let incX = pointDistance;
      let incY = 0;

      let currentPoint = new Point([minx, miny]);
      const shiftX =
        (((pointDistance / 2 + shiftVector.getCoordinates()[0]) % pointDistance) + pointDistance) % pointDistance;
      const shiftY =
        (((pointDistance / 2 + shiftVector.getCoordinates()[1]) % pointDistance) + pointDistance) % pointDistance;
      translate(currentPoint, shiftX, shiftY);

      let check = 0; // to prevent an infinite loop

      while (currentPoint.getCoordinates()[0] < maxx || currentPoint.getCoordinates()[1] < maxy) {
        // add point to list if in bounds
        if (inBounds(currentPoint, fieldPoly, boundaryDistance)) {
          points.push(currentPoint);
        }

        // check if we need to change directions
        if (incY === 0 && (currentPoint.getCoordinates()[0] > maxx || currentPoint.getCoordinates()[0] < minx)) {
          incY = pointDistance;
          incX = -incX;
        } else if (Math.abs(incY) > 0) {
          incY = 0;
        }

        // update current point
        currentPoint = currentPoint.clone();
        translate(currentPoint, incX, incY);

        if (check > 10000) {
          break;
        }

        check += 1;
      }

      // apply transforms in reverse order to all points
      for (const p of points) {
        // rotate and flip to change sample direction
        if ('N/S' === pathDirection) {
          p.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), false, true));
          p.rotate((-1 * Math.PI) / 2, getCenter(fieldPoly.getExtent()));
        }

        // flip to change the start corner
        if (startCorner === 'NW') {
          p.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), true, false));
        } else if (startCorner === 'NE') {
          p.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), true, true));
        } else if (startCorner === 'SW') {
          // no transform necessary
        } else if (startCorner === 'SE') {
          p.applyTransform((c, o, d) => flip(c, o, d, getCenter(fieldPoly.getExtent()), false, true));
        }

        // rotate for angle
        p.rotate(angle, getCenter(fieldPoly.getExtent()));
      }

      // Add points from this polygon to the total
      allPoints = allPoints.concat(points);
    }

    // Display results
    this.props.updateGridLayerFeatures([...allPlots, ...allPoints]);
    this.setState({ actual: allPoints.length, upToDate: true });
  }

  async applyGrid() {
    // send coordinates to server
    const points = this.props.gridLayerFeatures
      .map((f, idx) => {
        if (f instanceof Polygon || f instanceof LineString) {
          return null;
        }

        const lonlat = toLonLat(f.getCoordinates());

        return { id: (idx + 1).toString(), coords: { lat: lonlat[1], lon: lonlat[0] } };
      })
      .filter((point) => !!point);
    const mission = getCurrentMission();

    if (mission) {
      console.log(points);
      mission.delete_all_samples_and_cores();
      await mission.update_points(points, false);
      await mission.calculate_path();
      dispatchMissionUpdated();
      // calculate_path already fires dispatchCoresNeedUpdate() internally.
    }

    this.close();
  }

  close() {
    this.reset();
    this.setState({ gridContainerOpen: false });
    this.props.updateGridLayerFeatures([]);
  }

  render() {
    return (
      <React.Fragment>
        <MapButton
          tooltip="Generate Sample Grid"
          onClick={this.toggleGridContainer}
          toggled={this.state.gridContainerOpen}
          id={'toggleGridContainer'}
        >
          <HiOutlineViewGridAdd size={20} />
        </MapButton>
        {this.state.gridContainerOpen && (
          <ContrastPaper style={{ position: 'absolute', zIndex: 150 }}>
            <FormGroup row>
              <Box pt={1} pl={1}>
                <Typography variant="h5">Generate grid</Typography>
              </Box>
              <Box pt={1} pl={1} pb={1} className={'root'}>
                <Fab size="small" onClick={this.reset.bind(this)} title="Reset" className={'fabRoot'}>
                  <RotateLeftIcon />
                </Fab>
              </Box>
              <Box pt={1} pl={1} pb={1} pr={1}>
                <Fab size="small" title="Close" onClick={this.close.bind(this)} className={'fabRoot'}>
                  <CloseIcon />
                </Fab>
              </Box>
            </FormGroup>
            <FormGroup row>
              <Box pt={1} pl={1}>
                <TextField
                  label="acres:"
                  value={this.state.acres}
                  disabled
                  variant="outlined"
                  size="small"
                  inputProps={{ size: '6' }}
                />
              </Box>
              <Box pt={1} pl={1}>
                <TextField
                  label="target:"
                  value={Math.ceil(this.state.acres / this.state.density)}
                  disabled
                  variant="outlined"
                  size="small"
                  inputProps={{ size: '6' }}
                />
              </Box>
              <Box pt={1} pl={1} pr={1}>
                <TextField
                  label="actual:"
                  value={this.state.actual}
                  disabled
                  variant="outlined"
                  size="small"
                  inputProps={{ size: '6' }}
                />
              </Box>
            </FormGroup>
            <FormGroup row>
              <Box pt={1} pl={1}>
                <TextField
                  label="density:"
                  value={this.state.density}
                  size="small"
                  fullWidth
                  inputProps={{ size: '14' }}
                  InputProps={{ endAdornment: <InputAdornment position="end">ac/s</InputAdornment> }}
                  error={this.validate().density.error}
                  helperText={this.validate().density.text}
                  onChange={(e) => {
                    this.setState({ density: parseFloat(e.target.value || '0'), upToDate: false });
                  }}
                />
              </Box>
              <Box pt={1} pl={1} className={'root'}>
                <Fab
                  size="small"
                  className={'fabRoot'}
                  onClick={(e) => {
                    if (!isNaN(this.state.density)) {
                      this.setState({
                        density: round(this.state.density + this.state.densityIncrement, 1),
                        upToDate: false,
                      });
                    }
                  }}
                >
                  +
                </Fab>
              </Box>
              <Box pt={1} pl={1} pr={1}>
                <Fab
                  size="small"
                  className={'fabRoot'}
                  onClick={(e) => {
                    if (!isNaN(this.state.density) && round(this.state.density - this.state.densityIncrement, 1) > 0) {
                      this.setState({
                        density: round(this.state.density - this.state.densityIncrement, 1),
                        upToDate: false,
                      });
                    }
                  }}
                >
                  -
                </Fab>
              </Box>
            </FormGroup>
            <FormGroup row>
              <Box pt={1} pl={1}>
                <TextField
                  label="angle:"
                  value={this.state.angle}
                  size="small"
                  fullWidth
                  inputProps={{ size: '15' }}
                  InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }}
                  error={this.validate().angle.error}
                  helperText={this.validate().angle.text}
                  onChange={(e) => {
                    this.setState({ angle: parseFloat(e.target.value || '0'), upToDate: false });
                  }}
                />
              </Box>
              <Box pt={1} pl={1} className={'root'}>
                <Fab
                  size="small"
                  className={'fabRoot'}
                  onClick={(e) => {
                    if (!isNaN(this.state.angle)) {
                      this.setState({
                        angle: round((this.state.angle + this.state.angleIncrement + 90) % 90, 1),
                        upToDate: false,
                      });
                    }
                  }}
                >
                  +
                </Fab>
              </Box>
              <Box pt={1} pl={1} pr={1}>
                <Fab
                  size="small"
                  className={'fabRoot'}
                  onClick={(e) => {
                    if (!isNaN(this.state.angle)) {
                      this.setState({
                        angle: round((this.state.angle - this.state.angleIncrement + 90) % 90, 1),
                        upToDate: false,
                      });
                    }
                  }}
                >
                  -
                </Fab>
              </Box>
            </FormGroup>
            <FormGroup row>
              <Box pt={1} pl={1}>
                <TextField
                  label="min dist from bnd:"
                  value={this.state.boundaryDist}
                  size="small"
                  fullWidth
                  inputProps={{ size: '17' }}
                  InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                  error={this.validate().boundaryDist.error}
                  helperText={this.validate().boundaryDist.text}
                  onChange={(e) => {
                    this.setState({ boundaryDist: parseFloat(e.target.value || '0'), upToDate: false });
                  }}
                />
              </Box>
              <Box pt={1} pl={1} className={'root'}>
                <Fab
                  size="small"
                  className={'fabRoot'}
                  onClick={(e) => {
                    if (!isNaN(this.state.boundaryDist)) {
                      this.setState({
                        boundaryDist: round(this.state.boundaryDist + this.state.boundaryIncrement, 1),
                        upToDate: false,
                      });
                    }
                  }}
                >
                  +
                </Fab>
              </Box>
              <Box pt={1} pl={1} pr={1}>
                <Fab
                  size="small"
                  className={'fabRoot'}
                  onClick={(e) => {
                    if (
                      !isNaN(this.state.boundaryDist) &&
                      round(this.state.boundaryDist - this.state.boundaryIncrement, 1) >= 0
                    ) {
                      this.setState({
                        boundaryDist: round(this.state.boundaryDist - this.state.boundaryIncrement, 1),
                        upToDate: false,
                      });
                    }
                  }}
                >
                  -
                </Fab>
              </Box>
            </FormGroup>
            <FormGroup row>
              <Box pt={1} pl={1} width="50%">
                <FormControl fullWidth>
                  <InputLabel>start corner</InputLabel>
                  <Select
                    value={this.state.startCorner}
                    onChange={(e) => {
                      const newStartCorner = e.target.value;

                      if (typeof newStartCorner === 'string') {
                        this.setState({ startCorner: newStartCorner.toString(), upToDate: false });
                      }
                    }}
                  >
                    <MenuItem value="NW">NW</MenuItem>
                    <MenuItem value="NE">NE</MenuItem>
                    <MenuItem value="SW">SW</MenuItem>
                    <MenuItem value="SE">SE</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box pt={1} pl={1} pr={1} width="50%">
                <FormControl fullWidth>
                  <InputLabel>path direction</InputLabel>
                  <Select
                    value={this.state.pathDirection}
                    onChange={(e) => {
                      const pathDirection = e.target.value;

                      if (typeof pathDirection === 'string') {
                        this.setState({ pathDirection: pathDirection.toString(), upToDate: false });
                      }
                    }}
                  >
                    <MenuItem value="N/S">N/S</MenuItem>
                    <MenuItem value="E/W">E/W</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </FormGroup>
            <FormGroup row>
              <Box pt={1} pl={1} pr={1} width="100%">
                <FormControl fullWidth>
                  <InputLabel>Apply shift to:</InputLabel>
                  <Select
                    value={this.state.selectedPolyId}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Save current shift values before switching
                      const currentPolyId = this.state.selectedPolyId;
                      const newPolygonShifts = new Map(this.state.polygonShifts);

                      if (currentPolyId !== SELECTED_POLY_ID_ALL) {
                        newPolygonShifts.set(currentPolyId, {
                          shiftNorth: this.state.shiftNorth,
                          shiftEast: this.state.shiftEast,
                        });
                      }

                      // Load shift values for new selection
                      const savedShift = newPolygonShifts.get(value as string);
                      this.setState({
                        selectedPolyId: value as string,
                        polygonShifts: newPolygonShifts,
                        shiftNorth: savedShift?.shiftNorth || 0,
                        shiftEast: savedShift?.shiftEast || 0,
                        upToDate: false,
                      });
                    }}
                  >
                    <MenuItem value={SELECTED_POLY_ID_ALL}>All polygons</MenuItem>
                    {this.state.fields.length > 1 &&
                      this.state.fields.map((field) => {
                        const poly_id = field.getProperties().poly_id;

                        return (
                          <MenuItem key={poly_id} value={poly_id}>
                            Polygon {poly_id}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Box>
            </FormGroup>
            <FormGroup row>
              <Box pt={1} pl={1} pb={1}>
                <Fab
                  size="small"
                  title="Shift west"
                  className={'fabRoot'}
                  onClick={() => this.handleShiftChange('east', -this.state.shiftIncrement)}
                >
                  <ArrowLeftIcon />
                </Fab>
              </Box>
              <Box pt={1} pl={1} pb={1}>
                <Fab
                  size="small"
                  title="Shift east"
                  className={'fabRoot'}
                  onClick={() => this.handleShiftChange('east', this.state.shiftIncrement)}
                >
                  <ArrowRightIcon />
                </Fab>
              </Box>
              <Box pt={1} pl={1} pb={1}>
                <Fab
                  size="small"
                  title="Shift north"
                  className={'fabRoot'}
                  onClick={() => this.handleShiftChange('north', this.state.shiftIncrement)}
                >
                  <ArrowDropUpIcon />
                </Fab>
              </Box>
              <Box pt={1} pl={1} pb={1}>
                <Fab
                  size="small"
                  title="Shift south"
                  className={'fabRoot'}
                  onClick={() => this.handleShiftChange('north', -this.state.shiftIncrement)}
                >
                  <ArrowDropDownIcon />
                </Fab>
              </Box>
              <Box pt={1} pl={1} pb={1} pr={1}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={this.applyGrid.bind(this)}
                  disabled={!this.state.density}
                >
                  Apply
                </Button>
              </Box>
            </FormGroup>
          </ContrastPaper>
        )}
      </React.Fragment>
    );
  }
}
